<script setup lang="ts">
import { computed, defineProps, ref, Ref, watch } from "vue";
import { ChallengeConfiguration } from "@/types/v2/task/challenge/configuration.types"
import { Tank, TankGroup } from "@/types/v2";
import { useWeightForm } from "@/composables/tasks/challenge/useWeightForm";
import { useChallenge } from "@/composables/tasks/challenge/useChallenge";
import { useWeight } from "@/composables/tasks/challenge/useChallengeWeight";
import { useRecord } from "@/composables/tasks/challenge/useChallengeRecords";

import ModalBox from "@/components/v2/Modal/ModalBox.vue";

import { useEquipment } from "@/composables/tasks/useEquipment";

// ===================================================================
// Props
// ===================================================================
interface Props {
    configuration: ChallengeConfiguration | null;
    show: boolean;
    onClose?: () => void;
}
const props = defineProps<Props>();

// ===================================================================
// State
// ===================================================================
const showModal: Ref<boolean> = ref(props.show);

// ===================================================================
// Composables
// ===================================================================
const { tanks, getGroupsOfTank } = useChallenge();
const { selectedTank, selectedGroup, weight, comment, resetForm, weightId } = useWeightForm();
const { createWeightRecord } = useRecord();
const {
    getWeighedFish,
    isNoWeightType,
    isRandomWeightType,
    getTargetWeightAmount,
    shouldDisplayProgress,
    isIndividualWeightType,
    getWeighedFishInConfiguration,
    getTargetWeightAmountInConfiguration
} = useWeight();

const {
    hasWeightIdRequirement,
    getWeightId
} = useEquipment();

// ===================================================================
// Computed
// ===================================================================
const availableGroups = computed<TankGroup[]>(() => {
    if (!selectedTank.value) return [];
    return getGroupsOfTank(selectedTank.value.key)
});

interface GroupDropdownOption {
    key: string;
    label: string;
    rawGroup: TankGroup;
}

const getGroupTagCombination = (group: TankGroup | null | undefined): string => {
    if (!group?.tags || group.tags.length === 0) return 'Unmarked';

    const tagTitles = group.tags
        .filter(tag => tag.title !== 'Start Tag')
        .map(tag => tag.title);

    return tagTitles.length ? tagTitles.join(' + ') : 'Unmarked';
};

const availableGroupOptions = computed<GroupDropdownOption[]>(() => {
    return availableGroups.value.map(group => ({
        key: group.key,
        label: getGroupTagCombination(group),
        rawGroup: group
    }));
});

const selectedGroupOption = computed<{ key: string; label: string } | null>(() => {
    if (!selectedGroup.value) return null;

    return {
        key: selectedGroup.value.key,
        label: getGroupTagCombination(selectedGroup.value)
    };
});

const resolveConfigurationTank = (
    configuration: ChallengeConfiguration | null
): Tank | null => {
    if (!configuration) return null;
    if ('destinationTank' in configuration && configuration.destinationTank) {
        return configuration.destinationTank;
    }
    if ('tank' in configuration && configuration.tank) {
        return configuration.tank;
    }
    return null;
};

const resolveConfigurationGroup = (
    configuration: ChallengeConfiguration | null
): TankGroup | null => {
    if (!configuration) return null;
    if ('destinationGroup' in configuration && configuration.destinationGroup) {
        return configuration.destinationGroup;
    }
    return null;
};

const isTankLocked = computed(() => !!resolveConfigurationTank(props.configuration));
const isGroupLocked = computed(() => !!resolveConfigurationGroup(props.configuration));

const handleGroupOptionChange = ({ item }: { item: GroupDropdownOption }) => {
    selectedGroup.value = item?.rawGroup ?? null;
};

const handleTankChange = ({ item }: { item: Tank }) => {
    selectedTank.value = item ?? null;
    selectedGroup.value = null;
};

// ===================================================================
// Actions
// ===================================================================
// Modal
const closeModal = () => {
    if (props.onClose) props.onClose();
};

// Handle Record Weight
const recordWeight = (keepModalOpen = false): void => {
    // Get current weight_id from equipment configuration (in case it was updated)
    const currentWeightId = hasWeightIdRequirement() ? getWeightId() : null;

    createWeightRecord({
        weightId: currentWeightId || weightId.value || null,
        configurationKey: props.configuration?.key ?? null,
        tank: selectedTank.value ?? null,
        group: selectedGroup.value ?? null,
        comment: comment.value,
        weight: weight.value,
        timestamp: new Date().toISOString()
    });

    resetForm();

    // Reload weight_id after reset if requirement exists
    if (hasWeightIdRequirement()) {
        const equipmentWeightId = getWeightId();
        if (equipmentWeightId) {
            weightId.value = equipmentWeightId;
        }
    }

    if (!keepModalOpen) {
        return closeModal();
    }
};

// ===================================================================
// Watchers
// ===================================================================
watch(
    () => props.show,
    (newVal) => {
        showModal.value = newVal;
        // Load weight_id from equipment configuration when modal opens
        if (newVal && hasWeightIdRequirement()) {
            const equipmentWeightId = getWeightId();
            if (equipmentWeightId) {
                weightId.value = equipmentWeightId;
            }
        }
    }
);

watch(
    () => props.configuration,
    (newConfiguration) => {
        selectedTank.value = resolveConfigurationTank(newConfiguration);
        selectedGroup.value = resolveConfigurationGroup(newConfiguration);
    },
    { immediate: true }
);
</script>
<template>
    <ModalBox v-if="showModal" @close="closeModal">
        <template #header>
            <div class="flex items-center justify-between">
                <span>Record Weight &nbsp;</span>
                <span>
                    <template v-if="shouldDisplayProgress">
                        | Progress:
                        <template v-if="isIndividualWeightType">
                            {{ configuration ? getWeighedFishInConfiguration(configuration) : 0 }} / {{
                                configuration ? getTargetWeightAmountInConfiguration(configuration) : 0 }}
                        </template>
                        <template v-if="isRandomWeightType">
                            {{ getWeighedFish }} / {{ getTargetWeightAmount }}
                        </template>
                    </template>
                </span>
            </div>
        </template>

        <template #main>
            <template v-if="hasWeightIdRequirement()">
                <div class="mb-4">
                    <label class="block text-gray-700">
                        Weight ID
                    </label>
                    <input type="text" :value="weightId || ''" disabled
                        class="form-input p-2 mt-1 block w-full border border-gray-300 rounded bg-gray-100 cursor-not-allowed opacity-75"
                        placeholder="Set in Equipment page" />
                    <p class="text-xs text-gray-500 mt-1">Configure this value in the Equipment page</p>
                </div>
            </template>
            <template v-if="isNoWeightType || isRandomWeightType">
                <!-- Optional Tank Selection -->
                <div class="mb-4">
                    <label class="block text-gray-700">Tank (Optional):</label>
                    <ItemDropdown
                        :index="-1"
                        :label="''"
                        :items="tanks"
                        :itemKey="'tank'"
                        :neutralStyle="true"
                        :selectedItem="selectedTank ?? {}"
                        @fieldChange="handleTankChange"
                    />
                </div>

                <!-- Animal Group Selection -->
                <div v-if="selectedTank" class="mb-4">
                    <label class="block text-gray-700">
                        Animal Group {{ isIndividualWeightType ? "(Required)" : "(Optional)" }}:
                    </label>
                    <ItemDropdown
                        :index="-1"
                        :label="''"
                        :items="availableGroupOptions"
                        :itemKey="'tag'"
                        :neutralStyle="true"
                        :selectedItem="selectedGroupOption ?? {}"
                        @fieldChange="handleGroupOptionChange"
                    />
                </div>

                <!-- Weight Input -->
                <div class="mb-4">
                    <label class="block text-gray-700">Weight (Grams):</label>
                    <input type="number" v-model="weight"
                        class="form-input p-2 mt-1 block w-full border border-gray-300 rounded" />
                </div>

                <!-- Comment -->
                <div class="mb-4">
                    <label class="block text-gray-700">Comment:</label>
                    <textarea v-model="comment" class="form-input p-2 mt-1 block w-full border border-gray-300 rounded"
                        placeholder="Leave a comment"></textarea>
                </div>
            </template>

            <!-- For Individual Weights -->
            <template v-else-if="isIndividualWeightType">
                <!-- Required Tank Selection -->
                <div class="mb-4">
                    <label class="block text-gray-700">Tank (Required):</label>
                    <ItemDropdown :index="-1" :label="''" :disabled="isTankLocked" :items="tanks" :itemKey="'tank'"
                        :neutralStyle="true" :selectedItem="selectedTank ?? {}" @fieldChange="handleTankChange" />
                </div>

                <!-- Required Animal Group Selection -->
                <div v-if="selectedTank" class="mb-4">
                    <label class="block text-gray-700">Animal Group (Required):</label>
                    <ItemDropdown :index="-1" :label="''" :disabled="isGroupLocked" :items="availableGroupOptions"
                        :itemKey="'tag'" :neutralStyle="true" :selectedItem="selectedGroupOption || {}"
                        @fieldChange="handleGroupOptionChange" />
                </div>

                <!-- Weight Input -->
                <div class="mb-4">
                    <label class="block text-gray-700">Weight (Grams):</label>
                    <input type="number" v-model="weight"
                        class="form-input p-2 mt-1 block w-full border border-gray-300 rounded" />
                </div>

                <!-- Comment -->
                <div class="mb-4">
                    <label class="block text-gray-700">Comment:</label>
                    <textarea v-model="comment" class="form-input p-2 mt-1 block w-full border border-gray-300 rounded"
                        placeholder="Leave a comment"></textarea>
                </div>
            </template>
        </template>
        <template #footer>
            <div class="flex justify-between w-full">
                <div class="flex items-center gap-2">
                    <button @click="recordWeight(false)" type="button" :disabled="!weight"
                        class="disabled:opacity-25 text-white bg-primary hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center">
                        Save and close
                    </button>
                    <button @click="recordWeight(true)" type="button" :disabled="!weight"
                        class="disabled:opacity-25 text-white bg-green-600 hover:bg-green-500 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center">Save
                    </button>
                </div>
                <button @click="closeModal" type="button"
                    class="text-gray-500 bg-white hover:bg-gray-100 focus:ring-4 focus:outline-none focus:ring-blue-300 rounded-lg border border-gray-200 text-sm font-medium px-5 py-2.5 hover:text-gray-900 focus:z-10">
                    Back
                </button>
            </div>
        </template>
    </ModalBox>
</template>
